import React, { useState, useEffect } from 'react';
const API_URL = process.env.REACT_APP_API_URL;
const CATEGORY_CHOICES = [
  { value: 'furnishings', label: 'Furnishings' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'collectibles', label: 'Collectibles' },
  { value: 'sports', label: 'Sports and Hobbies' },
  { value: 'firearms', label: 'Firearms' },
  { value: 'clothes', label: 'Clothes' },
  { value: 'jewelry', label: 'Jewelry' },
  { value: 'non_farm_animals', label: 'Non‑farm Animals' },
  { value: 'other', label: 'Other' },
];

export default function HouseholdGoodsModal({
  isOpen,
  onClose,
  caseId,
  item,
  onSave,
  exemptions = [],
  scope,
}) {
  const [category, setCategory] = useState('furnishings');
  const [description, setDescription] = useState('');
  const [value, setValue] = useState('');
  const [exemptionCode, setExemptionCode] = useState('');
  const [exemptionAmount, setExemptionAmount] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (item) {
      setCategory(item.category || 'furnishings');
      setDescription(item.description || '');
      setValue(item.value != null ? item.value : '');
      setExemptionCode(item.exemption_code || '');
      setExemptionAmount(item.exemption_amount != null ? item.exemption_amount : '');
    } else {
      setCategory('furnishings');
      setDescription('');
      setValue('');
      setExemptionCode(scope === 'TN' ? '26-2-103' : '');
      setExemptionAmount('');
    }
  }, [item, scope]);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = item
        ? `${API_URL}/case/${caseId}/household-items/${item.id}/`
        : `${API_URL}/case/${caseId}/household-items/`;
      const method = item ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({
          category,
          description,
          value: value === '' ? null : parseFloat(value),
          exemption_code: exemptionCode,
          exemption_amount: exemptionAmount === '' ? null : parseFloat(exemptionAmount),
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      const saved = await res.json();
      onSave(saved);
      onClose();
    } catch (err) {
      console.error(err);
      alert('Error saving household item.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        <h3 className="text-lg font-semibold mb-4">
          {item ? 'Edit Household Item' : 'Add Household Item'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Category
            </label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              required
            >
              {CATEGORY_CHOICES.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              rows={3}
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Two-column row: Value and Exemption Amount */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Value Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Value
              </label>
              <input
                type="number"
                step="0.01"
                value={value}
                onChange={e => setValue(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            {/* Exemption Amount Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Exemption Amount
              </label>
              <input
                type="number"
                step="0.01"
                value={exemptionAmount}
                onChange={e => setExemptionAmount(e.target.value)}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Full-width Exemption Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Exemption Code
            </label>
            <select
              value={exemptionCode}
              onChange={e => setExemptionCode(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">None</option>
              {exemptions.map(ex => (
                <option key={ex.id} value={ex.law}>
                  {ex.asset_name} — {ex.law}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
